import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Leaf, Phone, MapPin, Globe, ChevronRight } from 'lucide-react';
import { TruncatedText, renderFormattedText } from '../components/shared/TextUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SpecialPageView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
    const loadData = async () => {
      try {
        const res = await axios.get(`${API}/special-pages/${slug}/dishes`);
        setPageData(res.data.page);
        setDishes(res.data.dishes);
      } catch (err) {
        console.error('Erreur chargement page:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  const getDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#2C2C2C] flex items-center justify-center">
      <p className="text-stone-400">Chargement...</p>
    </div>
  );

  if (!pageData) return (
    <div className="min-h-screen bg-[#2C2C2C] flex items-center justify-center">
      <p className="text-stone-400">Page non trouvee</p>
    </div>
  );

  // Filtrer les plats dont la date est passee (apres 13h30)
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const isPast1330 = now.getHours() > 13 || (now.getHours() === 13 && now.getMinutes() >= 30);
  const minDate = isPast1330 ? (() => { const d = new Date(now); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })() : todayStr;

  const futureDishes = dishes.filter(d => d.date >= minDate);

  // Grouper par date, puis par restaurant
  const byDate = {};
  futureDishes.forEach(d => {
    const dateKey = d.date || 'unknown';
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(d);
  });

  const sortedDates = Object.keys(byDate).sort();

  // Pour chaque date, grouper par restaurant et trier
  const getRestaurantGroups = (dateDishes) => {
    const byR = {};
    dateDishes.forEach(d => {
      const rId = d.restaurant?.name || 'unknown';
      if (!byR[rId]) byR[rId] = { restaurant: d.restaurant, dishes: [] };
      byR[rId].dishes.push(d);
    });
    return Object.values(byR).sort((a, b) => {
      if (userLocation && a.restaurant?.lat && b.restaurant?.lat) {
        return getDistance(userLocation.lat, userLocation.lng, a.restaurant.lat, a.restaurant.lng) -
               getDistance(userLocation.lat, userLocation.lng, b.restaurant.lat, b.restaurant.lng);
      }
      return (a.restaurant?.name || '').localeCompare(b.restaurant?.name || '');
    });
  };

  // Determiner le texte du service pour un groupe de plats
  // is_evening = "aussi le soir" = midi ET soir
  const getServiceText = (dishList) => {
    const hasBoth = dishList.some(d => d.is_both_services || d.is_evening);
    if (hasBoth) return 'Midi et Soir';
    return 'Uniquement à midi';
  };

  return (
    <div className="min-h-screen relative" data-testid="special-page-view">
      {/* Background vert matcha - identique à la bannière Home */}
      <div className="fixed inset-0 z-0 bg-[#4696C8]"></div>

      {/* Header sticky */}
      <div className="sticky top-0 z-30 shadow-lg bg-[#4696C8] border-b-2 border-white/30">
        {/* Bouton retour - style orange identique au bas de page */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <Link
            to="/plats"
            className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-white hover:bg-[#B8960F] px-4 py-1.5 rounded-full shadow text-xs font-semibold transition-colors"
            data-testid="back-to-plats"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Retour aux plats du jour
          </Link>
        </div>
        {/* Titre avec lapins à gauche et à droite */}
        <div className="text-center px-4 pb-4 pt-2">
          <div className="flex items-center justify-center gap-3">
            <img src="/lapin-paques.png" alt="" className="w-10 h-10 drop-shadow-xl object-contain" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg">
              Menus de Pâques
            </h1>
            <img src="/lapin-paques.png" alt="" className="w-10 h-10 drop-shadow-xl object-contain transform scale-x-[-1]" />
          </div>
          {pageData.description && (
            <div className="mt-1.5 text-white/75 text-xs sm:text-sm max-w-lg mx-auto leading-relaxed">
              {renderFormattedText(pageData.description)}
            </div>
          )}
        </div>
      </div>

      {/* Liste des plats — SANS séparateurs de date */}
      <div className="max-w-2xl mx-auto px-4 pb-12 pt-4 relative z-10">
        {sortedDates.length === 0 ? (
          <div className="text-center py-16 bg-[#F5F0E8] rounded-2xl shadow-md border border-[#D4AF37]/20">
            <img src="/lapin-paques.png" alt="" className="w-28 h-28 mx-auto mb-4 opacity-40 object-contain" />
            <p className="text-stone-600 text-lg font-medium">Les menus arrivent bientôt !</p>
            <p className="text-stone-400 text-sm mt-1">Les restaurants sont en train de préparer leurs menus de fête.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedDates.map(dateStr => {
              const dateDishes = byDate[dateStr];
              const restaurantGroups = getRestaurantGroups(dateDishes);

              return restaurantGroups.map(({ restaurant: r, dishes: rDishes }) => (
                      <div
                        key={r?.name}
                        className="rounded-xl overflow-hidden shadow-md border border-[#D4AF37]/15"
                        style={{ backgroundColor: '#F5F0E8' }}
                      >
                        {/* En-tête restaurant - style Home */}
                        <div className="text-center pt-4 pb-2 px-4">
                          {/* Date et service */}
                          <p className="text-xs font-semibold text-[#8B6914] uppercase tracking-wider mb-0.5">
                            {new Date(dateStr + 'T12:00:00').toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                          <p className="text-xs text-[#8B6914]/70 italic mb-1">
                            {getServiceText(rDishes)}
                          </p>
                          <h3
                            className="text-lg font-bold text-warmgray-dark flex items-center justify-center gap-1 cursor-pointer hover:text-[#8B6914] transition-colors"
                            onClick={() => r?.slug && navigate(`/restaurant/${r.slug}`)}
                          >
                            {r?.name} <ChevronRight className="h-4 w-4 text-stone-400" />
                          </h3>

                          {/* Commune + distance */}
                          <div className="flex items-center justify-center gap-2 mt-1 text-xs sm:text-sm">
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r?.address || '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-warmgray hover:text-[#8B6914] flex items-center gap-1 transition-colors"
                            >
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="font-medium">{r?.commune || ''}</span>
                              {userLocation && r?.lat && (
                                <span className="text-[#8B6914] font-bold">
                                  ({getDistance(userLocation.lat, userLocation.lng, r.lat, r.lng).toFixed(1)}km)
                                </span>
                              )}
                            </a>
                          </div>

                          {/* Téléphone + Site web */}
                          <div className="flex items-center justify-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm flex-wrap">
                            {r?.phone && (
                              <a href={`tel:${r.phone}`} className="text-warmgray hover:text-[#8B6914] flex items-center gap-1 transition-colors">
                                <Phone className="h-3 w-3 flex-shrink-0" />
                                <span>{r.phone}</span>
                              </a>
                            )}
                            {r?.website && (
                              <>
                                <span className="text-warmgray/30">|</span>
                                <a
                                  href={r.website.startsWith('http') ? r.website : `https://${r.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-warmgray hover:text-[#8B6914] flex items-center gap-1 transition-colors"
                                >
                                  <Globe className="h-3 w-3 flex-shrink-0" />
                                  <span>Site web</span>
                                </a>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Plats */}
                        <div className="mt-2 space-y-2 px-3 pb-4">
                          {rDishes.map((dish, idx) => (
                            <div key={dish.id || dish._id || `dish-${idx}`} className="bg-white/80 rounded-lg p-3 shadow-sm border border-warmgray/10">
                              {(dish.is_title_centered || dish.is_centered) ? (
                                <div>
                                  <div className="text-center">
                                    <h4 className="text-warmgray-dark text-base md:text-lg font-semibold">
                                      <TruncatedText text={dish.name} />
                                      {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600 inline ml-1" />}
                                    </h4>
                                    {dish.price > 0 && (
                                      <p className="text-[#8B6914] font-bold text-sm mt-0.5">
                                        CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price?.toFixed(2)}
                                      </p>
                                    )}
                                  </div>
                                  {dish.description && (
                                    <p className={`text-warmgray mt-0.5 text-xs leading-relaxed italic ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}>
                                      <TruncatedText text={dish.description} />
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex justify-between items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="text-warmgray-dark text-base md:text-lg font-semibold flex items-center gap-1.5">
                                      <TruncatedText text={dish.name} />
                                      {dish.is_vegetarian && <Leaf className="h-4 w-4 text-green-600 flex-shrink-0" />}
                                    </h4>
                                    {dish.description && (
                                      <p className={`text-warmgray mt-0.5 text-xs leading-relaxed italic ${(dish.is_description_centered || dish.is_centered) ? 'text-center' : ''}`}>
                                        <TruncatedText text={dish.description} />
                                      </p>
                                    )}
                                  </div>
                                  {dish.price > 0 && (
                                    <div className="flex items-center shrink-0">
                                      <span className="text-[#8B6914] font-bold text-sm whitespace-nowrap">
                                        CHF {Number.isInteger(dish.price) ? dish.price + '.-' : dish.price?.toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <Link
            to="/plats"
            className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-white hover:bg-[#B8960F] px-6 py-3 rounded-full shadow-lg text-sm font-semibold transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux plats du jour
          </Link>
          <p className="text-white/40 text-xs mt-4">&copy; {new Date().getFullYear()} AuMenu.ch</p>
        </div>
      </div>
    </div>
  );
}
